// UTILS
import { defineStore, storeToRefs } from "pinia"
import { ref, computed } from "vue"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { useSessionStorage } from "@/helpers/useSessionStorage"

// API
import { getAudio } from "@/api/s3/index"

// STORES
import { useUserStore } from "@/stores/user"
import { useHelpStore } from "@/components/af/Help/help.store"

// TYPES
import type { Ref } from "vue"
import type {
  Question,
  QuestionDetail,
} from "@/features/voice/store/voice.types"

// MACHINE
import { voiceActor } from "@/features/voice/machines/voiceMachine"

// API
import {
  getVoiceNextQuestion,
  getVoiceQuestions,
  postVoiceAudioQuestion,
  skipVoiceQuestion,
} from "@/api/viktor/assessment/assessment"

export const useQuestionsStore = defineStore("voiceQuestionsStore", () => {
  const { getUserUuid, getVoiceUuid } = storeToRefs(useUserStore())
  const { getSessionItem } = useSessionStorage()
  const { canAssessmentBePassed } = storeToRefs(useHelpStore())

  // Ref
  const items: Ref<Question[]> = ref([])
  const currentQuestion: Ref<QuestionDetail | undefined> = ref()

  // Computed
  const questionsLength = computed(() => items.value.length)
  const questionsFinished = computed(
    () =>
      items.value.filter(
        (item) =>
          item.status === "finish" ||
          item.status === "skip" ||
          item.status === "waiting_scores"
      ).length
  )
  const isLastQuestion = computed(
    () => questionsFinished.value >= questionsLength.value - 1
  )

  const recruiter = computed(() => items.value[0].recruiter)

  const voiceIsFinish = computed(() => {
    return !items.value.some((item) => item.status === "todo")
  })

  /**
   * Fetch all questions for the voice assessment
   * @returns Array of questions
   */
  async function fetchQuestions() {
    const data = await getVoiceQuestions(getUserUuid.value, getVoiceUuid.value)
    items.value = data.body

    return items.value
  }

  /**
   * Get the next question for the voice assessment
   * @returns The next question
   */
  async function getNextQuestion() {
    const data = await getVoiceNextQuestion(
      getUserUuid.value,
      getVoiceUuid.value
    )
    currentQuestion.value = data
    return currentQuestion.value
  }

  /**
   * Submit the answer to the current question
   * @param audio The audio file to submit
   * @param suspiciousActivity The suspicious activity data
   * @returns The response from the API
   */
  async function submit(
    audio: Blob,
    suspiciousActivity?: Record<string, unknown>
  ) {
    const response = await postVoiceAudioQuestion(
      getUserUuid.value,
      getVoiceUuid.value,
      currentQuestion.value?.question.uuid as string,
      audio,
      suspiciousActivity
    )

    return response
  }

  /**
   * Skip the current question
   */
  async function skip() {
    MixpanelInstance.track("voice_question-skip")
    await skipVoiceQuestion(
      getUserUuid.value,
      getVoiceUuid.value,
      currentQuestion.value?.question.uuid as string
    )
  }

  /**
   * Fetch the audios for the voice assessment
   */
  function fetchAudios() {
    getAudio({ test: "voice" })
  }

  /**
   * Go to the next question
   */
  async function goNext() {
    canAssessmentBePassed.value.voice = false // Disabled not passed assessment button

    if (isLastQuestion.value) {
      voiceActor.send({ type: "done" })
      return
    } else {
      await fetchQuestions()
      await getNextQuestion()
    }
  }

  /**
   * Check if the user was redirected to the voice assessment
   * @returns True if the user was redirected to the voice assessment
   */
  const wasRedirectedToVoice = getSessionItem("wasRedirectedToVoice") === true

  return {
    questions: items.value,
    currentQuestion,
    questionsLength,
    questionsFinished,
    isLastQuestion,
    voiceIsFinish,
    recruiter,
    fetchQuestions,
    getNextQuestion,
    fetchAudios,
    submit,
    skip,
    goNext,
    wasRedirectedToVoice,
  }
})
